import React, { useState, useRef, useEffect } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Form, message, Radio } from "antd";
import "./style.css";
import logo from "../../../Assets/Images/logo.svg";
import GenericDocumentManagerTable from "../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../Components/Cards/InputCard";
import { useNavigate } from "react-router-dom";
import ModuleSelctorCards from "../../../Components/Cards/ModuleSelectorCards";
import FuelIcon from "../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../Assets/Icons/car_repair.svg";
import logoCompany from "../../../Assets/Icons/companylogo.svg";
import { submitOnboardingRequest } from "../../../API/FleetManager/SignUp";
import { getModules, getDocumentCategories } from "../../../API/Config";
import { navixyApiKeyFieldProps } from "../../../Utlis/NavixyApiKeys";

const MODULE_ICONS = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const FleetManagerSignUp = ({ setUser }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [registrationData, setRegistrationData] = useState({});
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [vehicles, setVehicles] = useState(null);
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(true);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const isAnnual = pricingPeriod === "Annually Pricing";

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchModules = async () => {
      setModulesLoading(true);
      const res = await getModules();
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setModules(list);
      } else {
        message.error(res?.data?.message || "Failed to load modules.");
      }
      setModulesLoading(false);
    };

    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fleet-manager");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchModules();
    fetchDocumentCategories();
  }, []);

  const planOptions = modules.map((module) => {
    const isInactive = module.status !== "active";

    return {
      id: module.id,
      title: module.name,
      description: module.description,
      price: `${module.currency || "JMD"} ${isAnnual ? module.annual_price_per_vehicle : module.monthly_price_per_vehicle}/vehicle`,
      icon: (
        <img src={MODULE_ICONS[module.code] || FuelIcon} alt={module.code} />
      ),
      disabled: isInactive,
      tag: isInactive ? "Coming Soon" : undefined,
    };
  });

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVehicleChange = (value) => {
    const count = value === null || value === undefined ? null : Number(value);
    setVehicles(count);
  };

  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const registrationInformation = [
    {
      name: "legalBusinessName",
      label: "Legal Business Name",
      fullWidth: false,
      type: "text",
      placeholder: "Enter Legal Business Name",
      rules: [{ required: true, message: "Business name is required" }],
    },
    {
      name: "registrationNumber",
      label: "Registration Number",
      placeholder: "Enter Registration Number",
      fullWidth: false,
      type: "text",
      rules: [{ required: true, message: "Registration number is required" }],
    },
    {
      name: "contactNumber",
      label: "Company Contact Number",
      placeholder: "Enter Contact Number",
      fullWidth: false,
      type: "phone",
      rules: [
        { required: true, message: "Contact number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
    {
      name: "currency",
      label: "Currency",
      placeholder: "Enter amount",
      fullWidth: false,
      type: "currency",
      rules: [{ required: true, message: "Currency is required" }],
    },
    {
      name: "headQuarterAddress",
      label: "Headquarters Address",
      placeholder: "Enter Address Of Headquarters",
      fullWidth: false,
      type: "textarea",
      rows: 1,
      rules: [{ required: true, message: "Address is required" }],
    },
    {
      name: "dateIncorporated",
      label: "Date Incorporated",
      placeholder: "Select Date Incorporated",
      type: "date",
      rules: [{ required: true, message: "Date incorporated is required" }],
    },
    {
      name: "apiKeys",
      label: "API Key",
      placeholder: "Enter API key",
      type: "multi-input",
      addLabel: "Add another API key",
      ...navixyApiKeyFieldProps,
      fullWidth: true,
    },
  ];

  const contactInfoFields = [
    {
      name: "primarycontactperson",
      label: "Primary Contact Person",
      placeholder: "Enter Name Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "designation",
      label: "Designation",
      placeholder: "Enter Designation Of Primary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "businessemail",
      label: "Business Email",
      placeholder: "Enter Email Of Primary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "phonenumber",
      label: "Phone Number",
      placeholder: "Enter Contact Number Of Primary Contact Person",
      type: "phone",
      rules: [
        { required: true, message: "Phone number is required" },
        {
          validator: (_, value) => {
            if (!value || value.length >= 10) {
              return Promise.resolve();
            }
            return Promise.reject("Enter valid phone number");
          },
        },
      ],
    },
  ];

  const secondaryContactInformationFields = [
    {
      name: "secondarycontactperson",
      label: "Secondary Contact Person",
      placeholder: "Enter Name of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Name is required" }],
    },
    {
      name: "secondaryDesignation",
      label: "Designation",
      placeholder: "Enter Designation of Secondary Contact Person",
      type: "text",
      rules: [{ required: true, message: "Designation is required" }],
    },
    {
      name: "secondaryEmail",
      label: "Business Email",
      placeholder: "Enter Buisness Email Of Secondary Contact Person",
      type: "text",
      rules: [
        { required: true, message: "Email is required" },
        { type: "email", message: "Enter valid email" },
      ],
    },
    {
      name: "secondaryPhone",
      label: "Phone Number",
      placeholder: "Enter Contact Number of Secondary Contact Person",
      type: "phone",
      rules: [{ required: true, message: "Phone is required" }],
    },
  ];

  const PasswordFields = [
    {
      name: "password",
      label: "Password",
      placeholder: "Enter Password",
      type: "password",
      rules: [
        { required: true, message: "Password is required" },
        { min: 6, message: "Password must be at least 6 characters" },
      ],
    },
    {
      name: "confirmPassword",
      label: "Confirm Password",
      placeholder: "Re-enter Password",
      type: "password",
      dependencies: ["password"],
      rules: [
        { required: true, message: "Please confirm your password" },
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue("password") === value) {
              return Promise.resolve();
            }
            return Promise.reject("Passwords do not match");
          },
        }),
      ],
    },
  ];

  const handleCancel = () => {
    form.resetFields();
    setAvatar(null);
    setAvatarFile(null);
    setSelectedModuleIds([]);
    setVehicles(null);
    setDocuments([]);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedModuleIds.length) {
        message.error("Please select at least one module.");
        return;
      }

      if (!documents || documents.length === 0) {
        message.error("Please upload at least one document.");
        return;
      }

      if (!vehicles || vehicles < 1) {
        message.error("Please enter number of vehicles.");
        return;
      }

      const formData = new FormData();
      formData.append("legal_name", values.legalBusinessName);
      formData.append("registration_number", values.registrationNumber);
      formData.append("contact_email", values.businessemail);
      formData.append("contact_phone", values.phonenumber);
      formData.append("registration_contact_phone", values.contactNumber);
      formData.append("address", values.headQuarterAddress);
      if (values.apiKeys?.length) {
        formData.append("navixy_api_keys", JSON.stringify(values.apiKeys));
      }
      formData.append("currency", values.currency);
      formData.append(
        "date_incorporated",
        values.dateIncorporated ? values.dateIncorporated.format("YYYY-MM-DD") : "",
      );
      formData.append("primary_contact_person", values.primarycontactperson);
      formData.append("designation", values.designation);
      formData.append(
        "secondary_contact_person",
        values.secondarycontactperson,
      );
      formData.append("secondary_designation", values.secondaryDesignation);
      formData.append("secondary_contact_email", values.secondaryEmail);
      formData.append("secondary_contact_phone", values.secondaryPhone);
      formData.append("password", values.password);
      formData.append("confirm_password", values.confirmPassword);
      if (avatarFile) {
        formData.append("avatar", avatarFile);
      }
      formData.append("vehicle_limit", vehicles);
      formData.append("billing_cycle", isAnnual ? "annual" : "monthly");
      selectedModuleIds.forEach((id) => {
        formData.append("assigned_modules_ids", id);
      });
      documents.forEach((doc) => {
        formData.append("documents", doc.file);
      });
      formData.append(
        "document_categories",
        JSON.stringify(documents.map((doc) => doc.category)),
      );
      formData.append(
        "document_expiry_dates",
        JSON.stringify(documents.map((doc) => doc.expiryDate || null)),
      );

      setSubmitting(true);
      const res = await submitOnboardingRequest(formData);
      setSubmitting(false);

      if (res?.status === 200 || res?.status === 201) {
        navigate("/request-submitted");
      } else {
        const data = res?.data;
        if (typeof data?.detail === "string") {
          message.error(data.detail);
        } else if (data) {
          const messages = Object.entries(data).flatMap(([field, errors]) => {
            const list = Array.isArray(errors) ? errors : [errors];
            return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
          });
          message.error(
            messages.length
              ? messages.join(" | ")
              : "Failed to submit onboarding request.",
          );
        } else {
          message.error("Failed to submit onboarding request.");
        }
      }
    } catch (error) {
      message.error("Please check the form for missing fields.");
    }
  };

  return (
    <div className="sign-up">
      <Button
        className="signup-back"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
      />
      <div className="logo">
        <img src={logo} alt="logo" />
      </div>
      <div className="heading">Company Onboarding</div>
      <div className="signup-card">
        <Form form={form} layout="vertical">
          <div className="row">
            <div className="profile-card">
              <div className="profile-header">
                <div className="btn">
                  <Button
                    type="default"
                    className="upload-btn"
                    onClick={handleButtonClick}
                  >
                    Upload Photo
                  </Button>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <div className="profile-avatar">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt="avatar"
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                      }}
                    />
                  ) : (
                    "T"
                  )}
                </div>
              </div>
              <div className="profile-body"></div>
            </div>
          </div>
          <div>
            <div>
              <InputCard
                title="Registration Information"
                fields={registrationInformation}
              />
            </div>
            <div>
              <InputCard
                title="Contact Information"
                fields={contactInfoFields}
              />
            </div>
            <div>
              <InputCard
                title="Secondary Contact Information"
                fields={secondaryContactInformationFields}
              />
            </div>
            <div>
              <ModuleSelctorCards
                options={planOptions}
                multiple={true}
                selectedIds={selectedModuleIds}
                onChange={setSelectedModuleIds}
                showVehicleInput={true}
                vehicleCount={vehicles}
                onVehicleChange={handleVehicleChange}
                showFooter={true}
                footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
                headerExtra={
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    className="custom-segmented-control"
                    value={pricingPeriod}
                    onChange={(e) => setPricingPeriod(e.target.value)}
                    options={PRICING_TABS.map((tab) => ({
                      label: tab.replace(" Pricing", ""),
                      value: tab,
                    }))}
                  />
                }
              />
            </div>

            <div>
              <GenericDocumentManagerTable
                showUploadOnly={true}
                setDocuments={setDocuments}
                initialDocuments={documents}
                documentTypes={documentCategories}
              />
            </div>
            <div>
              <InputCard title="Password" fields={PasswordFields} />
            </div>
          </div>
          <div className="btns-row">
            <Button children="Cancel" onClick={handleCancel} />
            <Button
              children="Request Submit"
              onClick={() => handleSubmit()}
              loading={submitting}
              className="save-btn"
            />
          </div>
        </Form>
        <div className="logo-wrapper">
          <img src={logoCompany} alt="Logo Icon" className="logo-icon" />
          <div className="text">
            Powered by <span>CouSync</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetManagerSignUp;
